import { useEffect, useRef } from 'preact/hooks';
import type { Contact } from '../../types/contact';
import { MessageSquare, Trash2, X } from 'lucide-preact';

interface ContactContextMenuProps {
  x: number;
  y: number;
  contact: Contact | null;
  onClose: () => void;
  onDelete: () => void;
  onSelect: () => void;
}

export function ContactContextMenu({ x, y, contact, onClose, onDelete, onSelect }: ContactContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  if (!contact) return null;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('touchstart', handleClickOutside);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('touchstart', handleClickOutside);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Adjust menu position if near edge of viewport
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  const menuWidth = 200;
  const menuHeight = 140;

  const posX = Math.min(x, screenWidth - menuWidth - 10);
  const posY = Math.min(y, screenHeight - menuHeight - 10);

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black/20 backdrop-blur-[1px]">
      <div
        ref={menuRef}
        style={{ top: `${posY}px`, left: `${posX}px` }}
        class="fixed z-50 w-52 bg-slate-900 border border-slate-700/80 rounded-xl shadow-2xl p-1.5 animate-in fade-in zoom-in-95 duration-100"
      >
        {/* Header */}
        <div class="px-3 py-2 border-b border-slate-800 flex items-center justify-between">
          <span class="text-xs font-bold text-slate-200 truncate max-w-[140px]">
            {contact.name}
          </span>
          <button
            onClick={onClose}
            class="text-slate-400 hover:text-white p-0.5 rounded"
          >
            <X class="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Menu Options */}
        <div class="py-1 space-y-0.5">
          <button
            onClick={() => {
              onSelect();
              onClose();
            }}
            class="w-full text-left px-3 py-2 text-xs font-medium text-slate-200 hover:text-white hover:bg-slate-800 rounded-lg transition flex items-center gap-2 cursor-pointer"
          >
            <MessageSquare class="w-4 h-4 text-cyan-400" />
            <span>Abrir Conversa</span>
          </button>

          <button
            onClick={() => {
              onDelete();
              onClose();
            }}
            class="w-full text-left px-3 py-2 text-xs font-bold text-rose-400 hover:text-white hover:bg-rose-600/30 rounded-lg transition flex items-center gap-2 cursor-pointer"
          >
            <Trash2 class="w-4 h-4 text-rose-400" />
            <span>Excluir Conversa</span>
          </button>
        </div>
      </div>
    </div>
  );
}
